import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PackageBillingPhasesConfig from './components/PackageBillingPhasesConfig';
import { toast } from 'sonner';

/**
 * Page de gestion des configurations de facturation par phases
 * Accessible uniquement aux administrateurs
 */
const BillingPhasesManagement = () => {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);

  const breadcrumbItems = [
    { label: 'Tableau de bord', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Gestion de la facturation', path: '/billing-overview', icon: 'CreditCard' },
    { label: 'Phases de facturation', path: '/billing-phases-management', icon: 'Settings' }
  ];

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('commercial_packages')
        .select(`
          *,
          countries (
            id,
            country_name,
            country_code,
            currency_code
          ),
          billing_phases:package_billing_phases (
            id,
            phase_number,
            phase_name,
            aligner_range_from,
            aligner_range_to,
            phase_price,
            is_active
          )
        `)
        .eq('status', 'active')
        .order('tier', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Compter le nombre de phases actives pour chaque package
      const packagesWithStats = data.map(pkg => ({
        ...pkg,
        activePhases: pkg.billing_phases?.filter(p => p.is_active).length || 0,
        totalPhasePrice: pkg.billing_phases
          ?.filter(p => p.is_active)
          .reduce((sum, p) => sum + parseFloat(p.phase_price || 0), 0) || 0
      }));

      setPackages(packagesWithStats);
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Erreur lors du chargement des packages');
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      bronze: 'bg-amber-700 text-white',
      silver: 'bg-gray-400 text-gray-900',
      gold: 'bg-yellow-500 text-gray-900',
      platinum: 'bg-purple-600 text-white'
    };
    return colors[tier] || 'bg-gray-500 text-white';
  };

  const formatCurrency = (value, currencyCode = 'EUR') => {
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currencyCode || 'EUR',
      }).format(value ?? 0);
    } catch {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
      }).format(value ?? 0);
    }
  };

  const handleConfigurePackage = (pkg) => {
    setSelectedPackage(pkg);
    setShowConfigModal(true);
  };

  const handleCloseModal = () => {
    setShowConfigModal(false);
    setSelectedPackage(null);
  };

  const handleSaveConfig = async () => {
    await fetchPackages();
    handleCloseModal();
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />

      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Breadcrumb items={breadcrumbItems} />

        <div className="px-6 py-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Configuration des phases de facturation
              </h1>
              <p className="text-muted-foreground">
                Définissez les paliers de facturation pour chaque package commercial
              </p>
            </div>
            <Button
              variant="outline"
              onClick={fetchPackages}
              iconName="RefreshCw"
              iconPosition="left"
              disabled={loading}
            >
              Actualiser
            </Button>
          </div>

          {/* Info banner */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-primary mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-foreground mb-1">
                  Facturation progressive par aligneurs
                </h3>
                <p className="text-sm text-muted-foreground">
                  Configurez des paliers de facturation basés sur le nombre d'aligneurs produits. 
                  Chaque phase sera facturée automatiquement lorsque la production entre dans sa plage définie.
                </p>
              </div>
            </div>
          </div>

          {/* Packages grid */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="Loader" size={32} className="animate-spin text-primary mr-3" />
              <span className="text-muted-foreground">Chargement des packages...</span>
            </div>
          ) : packages.length === 0 ? (
            <div className="text-center py-16">
              <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Aucun package actif
              </h3>
              <p className="text-muted-foreground">
                Créez des packages commerciaux pour configurer leur facturation
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-all"
                >
                  {/* Package header */}
                  <div className="p-6 border-b border-border bg-muted/30">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-foreground mb-1">
                          {pkg.package_name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {pkg.package_code}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${getTierColor(pkg.tier)}`}>
                        {pkg.tier}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Globe" size={14} />
                      <span>{pkg.countries?.country_name}</span>
                      <span className="text-xs">({pkg.countries?.currency_code})</span>
                    </div>
                  </div>

                  {/* Package stats */}
                  <div className="p-6">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="text-center p-3 bg-muted/30 rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Phases configurées</p>
                        <p className="text-2xl font-bold text-foreground">
                          {pkg.activePhases}
                        </p>
                      </div>
                      <div className="text-center p-3 bg-muted/30 rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Prix total</p>
                        <p className="text-lg font-bold text-primary">
                          {formatCurrency(pkg.totalPhasePrice, pkg.countries?.currency_code)}
                        </p>
                      </div>
                    </div>

                    {/* Phases preview */}
                    {pkg.billing_phases && pkg.billing_phases.filter(p => p.is_active).length > 0 ? (
                      <div className="mb-4">
                        <p className="text-xs font-semibold text-muted-foreground mb-2">
                          Aperçu des phases :
                        </p>
                        <div className="space-y-1">
                          {pkg.billing_phases
                            .filter(p => p.is_active)
                            .sort((a, b) => a.phase_number - b.phase_number)
                            .slice(0, 3)
                            .map((phase) => (
                              <div
                                key={phase.id}
                                className="flex items-center justify-between text-xs bg-muted/20 rounded px-2 py-1"
                              >
                                <span className="text-muted-foreground">
                                  {phase.aligner_range_from}-{phase.aligner_range_to} aligneurs
                                </span>
                                <span className="font-semibold text-foreground">
                                  {formatCurrency(phase.phase_price, pkg.countries?.currency_code)}
                                </span>
                              </div>
                            ))}
                          {pkg.billing_phases.filter(p => p.is_active).length > 3 && (
                            <p className="text-xs text-muted-foreground text-center pt-1">
                              +{pkg.billing_phases.filter(p => p.is_active).length - 3} phase(s) de plus
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="mb-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Icon name="AlertTriangle" size={14} className="text-warning" />
                          <p className="text-xs text-warning">
                            Aucune phase configurée
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Configure button */}
                    <Button
                      variant={pkg.activePhases > 0 ? 'outline' : 'default'}
                      fullWidth
                      onClick={() => handleConfigurePackage(pkg)}
                      iconName={pkg.activePhases > 0 ? 'Settings' : 'Plus'}
                      iconPosition="left"
                    >
                      {pkg.activePhases > 0 ? 'Modifier la configuration' : 'Configurer les phases'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Configuration Modal */}
      {showConfigModal && selectedPackage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in overflow-y-auto">
          <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-4xl my-8 animate-slide-in">
            <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  Configuration des phases de facturation
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedPackage.package_name} ({selectedPackage.countries?.country_name})
                </p>
              </div>
              <button
                onClick={handleCloseModal}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <PackageBillingPhasesConfig
                packageId={selectedPackage.id}
                packageName={selectedPackage.package_name}
                currencyCode={selectedPackage.countries?.currency_code}
                onSave={handleSaveConfig}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingPhasesManagement;